<script setup lang="ts">
import { reactive } from 'vue'
import type {
  StaffFileProcessingResponse,
  UserIncidentFootage,
  UserIncidentImage,
  ViewIncidentFileResponse
} from '@roadfootage/api-client'
import { MediaProcessingStatus } from '@roadfootage/api-client'
import { formatFileSize } from '@/utils/format'
import ProcessingStepDetail from './ProcessingStepDetail.vue'

type MediaFile = UserIncidentFootage | UserIncidentImage

const props = defineProps<{
  file: MediaFile
  urlData: ViewIncidentFileResponse | undefined
  mediaStatus: MediaProcessingStatus | null | undefined
  /** Pipeline step and percent from GET /admin/incident-reports/{id}/processing. */
  processing?: StaffFileProcessingResponse
  isSelected: boolean
  isViewed: boolean
  isPrimary: boolean
  isLoading: boolean
  mediaType: 'video' | 'image'
}>()

const emit = defineEmits<{
  select: []
  delete: []
  reprocess: []
  setPrimary: []
  download: []
}>()

const HOVER_DELAY_MS = 600
const hoverState = reactive({ intentToPlay: false, isVideoReady: false })
let hoverTimeout: ReturnType<typeof setTimeout> | null = null

const onEnter = () => {
  if (!props.urlData?.previewUrl) return
  hoverTimeout = setTimeout(() => {
    hoverState.intentToPlay = true
  }, HOVER_DELAY_MS)
}

const onLeave = () => {
  if (hoverTimeout) clearTimeout(hoverTimeout)
  hoverState.intentToPlay = false
  hoverState.isVideoReady = false
}

const onVideoPlaying = () => {
  hoverState.isVideoReady = true
}

const statusBadgeProps = (status: MediaProcessingStatus | null | undefined) => {
  switch (status) {
    case MediaProcessingStatus.Complete:
      return { label: 'Ready', color: 'positive' }
    case MediaProcessingStatus.Processing:
      return { label: 'Processing', color: 'info' }
    case MediaProcessingStatus.Failed:
      return { label: 'Failed', color: 'negative' }
    default:
      return { label: 'Pending', color: 'warning' }
  }
}

const fallbackIcon = props.mediaType === 'video' ? 'mdi-video' : 'mdi-image'
const offIcon = props.mediaType === 'video' ? 'mdi-video-off' : 'mdi-image-off'
</script>

<template>
  <q-card
    flat
    bordered
    class="cursor-pointer file-card"
    style="width: 130px"
    :style="isSelected ? 'outline: 2px solid var(--q-primary)' : ''"
    @click="emit('select')"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <!-- Thumbnail area -->
    <div class="relative-position overflow-hidden bg-grey-9" style="height: 78px">
      <!-- Loading / no URL -->
      <div v-if="isLoading || !urlData" class="absolute-full flex flex-center">
        <q-spinner v-if="isLoading" size="sm" color="grey-5" />
        <q-icon v-else :name="offIcon" size="sm" color="grey-6" />
      </div>

      <!-- Pending / Processing / Failed placeholder -->
      <div
        v-else-if="mediaStatus !== MediaProcessingStatus.Complete"
        class="absolute-full flex flex-center column bg-grey-9 text-grey-5 q-gutter-y-xs"
      >
        <q-spinner
          v-if="mediaStatus === MediaProcessingStatus.Processing"
          size="xs"
          color="grey-5"
        />
        <q-icon
          v-else-if="mediaStatus === MediaProcessingStatus.Failed"
          name="mdi-alert-circle-outline"
          size="xs"
          color="negative"
        />
        <q-icon v-else name="mdi-clock-outline" size="xs" />
        <div style="font-size: 9px">
          {{ mediaStatus === MediaProcessingStatus.Failed ? 'Failed' : 'Pending' }}
        </div>
      </div>

      <!-- Ready -->
      <template v-else>
        <q-img
          v-if="urlData!.thumbnailUrl"
          :src="urlData!.thumbnailUrl!"
          class="absolute-full crossfade-layer"
          :class="{ 'opacity-0': hoverState.isVideoReady }"
          fit="cover"
          no-spinner
        />
        <div v-else class="absolute-full flex flex-center bg-grey-8">
          <q-icon :name="fallbackIcon" size="sm" color="grey-6" />
        </div>
        <video
          v-if="urlData!.previewUrl && hoverState.intentToPlay"
          :src="urlData!.previewUrl!"
          class="absolute-full crossfade-layer thumb-video"
          :class="{ 'opacity-0': !hoverState.isVideoReady }"
          autoplay
          muted
          loop
          playsinline
          preload="metadata"
          @playing="onVideoPlaying"
        />
      </template>

      <!-- Delete button (visible on hover via CSS) -->
      <q-btn
        flat
        round
        icon="mdi-delete"
        color="white"
        size="xs"
        class="absolute-top-right action-btn"
        style="background: rgba(0, 0, 0, 0.5)"
        @click.stop="emit('delete')"
      >
        <q-tooltip>Remove file</q-tooltip>
      </q-btn>

      <!-- Reprocess button (visible on hover via CSS) -->
      <q-btn
        v-if="true || props.mediaStatus !== MediaProcessingStatus.Complete"
        flat
        round
        icon="mdi-refresh"
        color="white"
        size="xs"
        class="absolute-top-left action-btn"
        style="background: rgba(0, 0, 0, 0.5)"
        @click.stop="emit('reprocess')"
      >
        <q-tooltip>Reprocess file</q-tooltip>
      </q-btn>

      <!-- Viewed indicator -->
      <q-icon
        v-if="isViewed"
        name="mdi-check-circle"
        color="positive"
        size="xs"
        class="absolute-bottom-left q-ma-xs"
        style="filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))"
      />

      <!-- Primary file indicator -->
      <q-btn
        flat
        round
        :icon="isPrimary ? 'mdi-star' : 'mdi-star-outline'"
        :color="isPrimary ? 'amber' : 'white'"
        size="xs"
        class="absolute-bottom-right action-btn"
        :class="{ 'always-visible': isPrimary }"
        style="background: rgba(0, 0, 0, 0.5)"
        @click.stop="emit('setPrimary')"
      >
        <q-tooltip>{{ isPrimary ? 'Primary media' : 'Set as primary media' }}</q-tooltip>
      </q-btn>
    </div>

    <!-- File info -->
    <q-card-section class="q-pa-xs">
      <div
        class="text-caption text-weight-medium ellipsis"
        :title="(file as any).uploadFileName ?? ''"
      >
        {{ (file as any).uploadFileName || 'Unknown' }}
      </div>
      <div class="text-caption text-grey-6">
        {{
          (file as any).uploadFileSize != null ? formatFileSize((file as any).uploadFileSize) : '—'
        }}
      </div>
      <div class="text-caption text-grey-6 ellipsis" :title="(file as any).uploadMimeType ?? ''">
        {{ (file as any).uploadMimeType || '—' }}
      </div>
      <div class="row items-center justify-between no-wrap q-mt-xs">
        <q-badge v-bind="statusBadgeProps(mediaStatus)" outline />
        <q-btn
          flat
          round
          dense
          size="sm"
          icon="mdi-download"
          :aria-label="`Download ${(file as any).uploadFileName || 'file'}`"
          @click.stop="emit('download')"
        >
          <q-tooltip>Download…</q-tooltip>
        </q-btn>
      </div>
      <ProcessingStepDetail
        v-if="mediaStatus !== MediaProcessingStatus.Complete"
        :detail="processing"
      />
    </q-card-section>
  </q-card>
</template>

<style lang="scss" scoped>
.crossfade-layer {
  transition: opacity 0.8s ease-in-out;
  will-change: opacity;
}
.opacity-0 {
  opacity: 0;
  pointer-events: none;
}
.thumb-video {
  object-fit: cover;
}
.action-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
}
.file-card:hover .action-btn {
  opacity: 1;
}
.action-btn.always-visible {
  opacity: 1;
}
</style>
